import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/api";
import VaultLogo from "@/components/VaultLogo";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await axios.post(`${API_BASE}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen noise grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--brand-ink)] text-white">
        <Link to="/" className="font-display text-2xl font-bold flex items-center gap-2" data-testid="forgot-brand">
          <VaultLogo size={28} dark/>JobVault
        </Link>
        <div>
          <p className="label-tiny text-[var(--pastel-yellow)] mb-3">password rescue</p>
          <h2 className="font-display text-5xl font-semibold leading-[1.05]">
            forgot it?<br/>
            <span className="text-[var(--pastel-yellow)]">happens to the best of us.</span>
          </h2>
          <p className="mt-8 text-white/70 max-w-md leading-relaxed">
            type your email. we send a link. you set a new one. takes 30 seconds.
          </p>
        </div>
        <p className="font-mono-d text-xs text-white/40">link expires in 1 hour · one-time use</p>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <p className="label-tiny text-[var(--brand-muted)] mb-2">/forgot password</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight mb-8">reset your vault.</h1>

          {sent ? (
            <div className="card-hard rounded-md p-6 bg-[var(--pastel-mint)]" data-testid="forgot-success">
              <p className="label-tiny mb-2 flex items-center gap-1.5">
                <EnvelopeSimple size={12} weight="duotone"/> /check your inbox
              </p>
              <h3 className="font-display text-2xl font-semibold tracking-tight">link incoming.</h3>
              <p className="text-sm mt-3 text-[var(--brand-muted)]">
                if <b className="font-mono-d">{email}</b> is in our system, a reset link is on its way.
                check spam / promotions if you don&apos;t see it within a minute.
              </p>
              <p className="text-sm mt-3 text-[var(--brand-muted)]">
                the link expires in 1 hour.
              </p>
              <Link to="/login" data-testid="forgot-back-login"
                className="mt-5 inline-block btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm">
                back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              {err && <div data-testid="forgot-error" className="card-hard rounded-md p-3 bg-[var(--pastel-peach)] text-sm mb-4">{err}</div>}
              <label className="label-tiny block mb-2">email</label>
              <input data-testid="forgot-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full card-hard rounded-md px-4 py-3 mb-6 focus:ring-2 focus:ring-[var(--brand-primary)]"/>
              <button data-testid="forgot-submit" disabled={loading} type="submit"
                className="w-full btn-hard bg-[var(--brand-primary)] text-white rounded-md py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? "sending…" : <>send reset link <ArrowRight weight="bold" size={16}/></>}
              </button>
              <p className="mt-6 text-sm text-[var(--brand-muted)]">
                remembered it? <Link to="/login" data-testid="forgot-to-login" className="text-[var(--brand-ink)] underline font-medium">log in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
